import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

export interface RankedTemplate {
  bomTemplateId: number;
  bomName: string;
  bomDesc: string | null;
  displayGroup: string;
}

export const bomTemplatesApi = {
  ranked: async (q: { productId?: number; categoryId?: number; parentCategoryId?: number }): Promise<RankedTemplate[]> => {
    const p = new URLSearchParams();
    if (q.productId != null) p.set("productId", String(q.productId));
    if (q.categoryId != null) p.set("categoryId", String(q.categoryId));
    if (q.parentCategoryId != null) p.set("parentCategoryId", String(q.parentCategoryId));
    const res = await apiFetch<{ items: RankedTemplate[]; total: number }>(`/api/bom-templates?${p.toString()}`);
    return res.items;
  },
};

export function useRankedTemplates(productId: number | null) {
  return useQuery({
    queryKey: ["bom-templates", "ranked", productId],
    queryFn: () => bomTemplatesApi.ranked({ productId: productId! }),
    enabled: !!productId,
  });
}

// ── Admin CRUD (Plan III-A) ────────────────────────────────────────────────

// Read detail/row — mirrors BomTemplateDetailDto. INCLUDES lineItemsJson so it
// round-trips on READ. RowVersion is `byte[]?` on the DTO (no separate base64
// property) → ASP.NET camelCase Web JSON serializes it to the JSON property
// `rowVersion` with a base64-STRING value, so the field is `rowVersion`.
export interface BomTemplateDetail {
  bomTemplateId: number;
  bomName: string;
  bomDesc: string | null;
  indentBom: boolean;
  promoBom: boolean;
  sheetBom: boolean;
  currencyCode: string;
  markupPct: number;
  quantities: string | null;
  overs: string | null;
  lineItemsJson: string | null;
  rowVersion: string | null;
}

// Create — header fields. lineItemsJson allowed null on create; new templates
// have no line items in III-A.
export interface CreateBomTemplateBody {
  bomName: string;
  bomDesc: string | null;
  indentBom: boolean;
  promoBom: boolean;
  sheetBom: boolean;
  currencyCode: string;
  markupPct: number;
  quantities: string | null;
  overs: string | null;
  lineItemsJson: string | null;
}

// Update — line items are now editable (III-B): includes lineItemsJson. Clear
// flags let the server null out BomDesc/Quantities/Overs (the merge default
// leaves a null/absent field unchanged).
export interface UpdateBomTemplateBody {
  bomName: string;
  bomDesc: string | null;
  indentBom: boolean;
  promoBom: boolean;
  sheetBom: boolean;
  currencyCode: string;
  markupPct: number;
  quantities: string | null;
  overs: string | null;
  lineItemsJson: string | null;
  clearBomDesc?: boolean;
  clearQuantities?: boolean;
  clearOvers?: boolean;
}

// Usage (III-D Task 2) — GET /api/bom-templates/{id}/usage returns resolved
// product/category names for each consumer of the template.
export interface BomTemplateUsage {
  bomTemplateId: number;
  productId: number;
  productName: string | null;
  categoryId: number | null;
  categoryName: string | null;
  parentCategoryId: number | null;
  parentCategoryName: string | null;
}

// Ranked list endpoint with no product context → all rows DisplayGroup
// "z) all templates". Returns the wrapped { items, total } shape.
export interface BomTemplateAdminPage {
  items: RankedTemplate[];
  total: number;
}

export const bomTemplateKeys = {
  adminList: (q: string, page: number) => ["bom-templates", "admin", q, page] as const,
};

export const bomTemplatesAdminApi = {
  listAdmin: (q: string, page = 0, pageSize = 100): Promise<BomTemplateAdminPage> =>
    apiFetch<BomTemplateAdminPage>(
      `/api/bom-templates?${q ? `q=${encodeURIComponent(q)}&` : ""}page=${page}&pageSize=${pageSize}`,
    ),
  get: (id: number): Promise<BomTemplateDetail> =>
    apiFetch<BomTemplateDetail>(`/api/bom-templates/${id}`),
  create: (body: CreateBomTemplateBody): Promise<BomTemplateDetail> =>
    apiFetch<BomTemplateDetail>("/api/bom-templates", { method: "POST", body: JSON.stringify(body) }),
  // body now carries lineItemsJson (editable in III-B); clear flags null out simple fields.
  update: (id: number, body: UpdateBomTemplateBody, rvToken: string | null): Promise<BomTemplateDetail> =>
    apiFetch<BomTemplateDetail>(`/api/bom-templates/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: rvToken ? { "If-Match": rvToken } : {},
    }),
  remove: (id: number, rvToken: string | null): Promise<void> =>
    apiFetch<void>(`/api/bom-templates/${id}`, {
      method: "DELETE",
      headers: rvToken ? { "If-Match": rvToken } : {},
    }),
  duplicate: (id: number): Promise<BomTemplateDetail> =>
    apiFetch<BomTemplateDetail>(`/api/bom-templates/${id}/duplicate`, { method: "POST" }),
  usage: (id: number): Promise<BomTemplateUsage[]> =>
    apiFetch<BomTemplateUsage[]>(`/api/bom-templates/${id}/usage`),
};

export function useBomTemplatesAdmin(q: string, page = 0) {
  return useQuery({
    queryKey: bomTemplateKeys.adminList(q, page),
    queryFn: () => bomTemplatesAdminApi.listAdmin(q, page),
  });
}
